import {
  fetchApiWithParams,
  getApiBlob,
  patchApiFunctional,
  postApiFunctional,
} from "../../hooks/GlobalHooks";
import type {
  MealCompletePickupApiResponse,
  MealCompletePickupPayload,
  MealMonitoringDetailApiResponse,
  MealMonitoringDetailPayload,
} from "./types/mealMonitoringDetailType";
import type {
  MealConfirmOrderApiResponse,
  MealMonitoringApiResponse,
  MealMonitoringPayload,
  MealUpdateConfigurationApiResponse,
  MealUpdateConfigurationPayload,
} from "./types/mealMonitoringType";
import type {
  MealPickUpApiResponse,
  MealPickUpPayloadUnion,
} from "./types/mealPickUpType";
import type {
  MealCheckActiveOraApiResponse,
  MealTodayApiResponse,
  MealTodayPayload,
} from "./types/mealTodayType";

const BASE_URL = process.env.REACT_APP_API_URL_2;

const mealApi = {
  checkActiveOraUrl: `${BASE_URL}/check-active-ora`,
  mealTodayUrl: `${BASE_URL}/sus/meals/today`,
  mealPickupCreateUrl: `${BASE_URL}/sus/meals/item/create`,
  mealPickupMenuUrl: `${BASE_URL}/sus/meals/mobile/complete-pick-meals`,
  mealMonitoringUrl: `${BASE_URL}/sus/meals/monitoring`,
  mealMonitoringDetailUrl: (uuid: string) =>
    `${BASE_URL}/sus/meals/detail/${uuid}`,
  mealUpdateConfigurationUrl: (uuid: string) =>
    `${BASE_URL}/sus/meals/update/${uuid}`,
  mealConfirmOrderUrl: (uuid: string) =>
    `${BASE_URL}/sus/meals/confirm-order/${uuid}`,
  mealDownloadFormUrl: (uuid: string) =>
    `${BASE_URL}/sus/meals/export-form/${uuid}`,

  getCheckActiveOra: async (
    signal?: AbortSignal,
  ): Promise<MealCheckActiveOraApiResponse> => {
    return fetchApiWithParams<MealCheckActiveOraApiResponse, null>(
      mealApi.checkActiveOraUrl,
      null,
      signal,
    );
  },

  getMealToday: async (
    payload: MealTodayPayload,
    signal?: AbortSignal,
  ): Promise<MealTodayApiResponse> => {
    return postApiFunctional<MealTodayApiResponse, MealTodayPayload>(
      mealApi.mealTodayUrl,
      payload,
      signal,
    );
  },

  createMealPickup: async (
    payload: MealPickUpPayloadUnion,
    signal?: AbortSignal,
  ): Promise<MealPickUpApiResponse> => {
    return postApiFunctional<MealPickUpApiResponse, MealPickUpPayloadUnion>(
      mealApi.mealPickupCreateUrl,
      payload,
      signal,
    );
  },

  getMealMonitoring: async (
    payload: MealMonitoringPayload,
    signal?: AbortSignal,
  ): Promise<MealMonitoringApiResponse> => {
    return postApiFunctional<MealMonitoringApiResponse, MealMonitoringPayload>(
      mealApi.mealMonitoringUrl,
      payload,
      signal,
    );
  },

  updateMealConfiguration: async (
    uuid: string,
    payload: MealUpdateConfigurationPayload,
    signal?: AbortSignal,
  ): Promise<MealUpdateConfigurationApiResponse> => {
    return patchApiFunctional<
      MealUpdateConfigurationApiResponse,
      MealUpdateConfigurationPayload
    >(mealApi.mealUpdateConfigurationUrl(uuid), payload, signal);
  },

  mealConfirmOrder: async (
    uuid: string,
    signal?: AbortSignal,
  ): Promise<MealConfirmOrderApiResponse> => {
    return fetchApiWithParams<MealConfirmOrderApiResponse, null>(
      mealApi.mealConfirmOrderUrl(uuid),
      null,
      signal,
    );
  },

  downloadMealForm: async (
    uuid: string,
    signal?: AbortSignal,
  ): Promise<{
    blob: Blob;
    contentDisposition: string | null;
  }> => {
    return getApiBlob(mealApi.mealDownloadFormUrl(uuid), signal);
  },

  getMealMonitoringDetail: async (
    uuid: string,
    payload: MealMonitoringDetailPayload,
    signal?: AbortSignal,
  ): Promise<MealMonitoringDetailApiResponse> => {
    return postApiFunctional<
      MealMonitoringDetailApiResponse,
      MealMonitoringDetailPayload
    >(mealApi.mealMonitoringDetailUrl(uuid), payload, signal);
  },

  mealCompletePickupMenu: async (
    payload: MealCompletePickupPayload,
    signal?: AbortSignal,
  ): Promise<MealCompletePickupApiResponse> => {
    return postApiFunctional<
      MealCompletePickupApiResponse,
      MealCompletePickupPayload
    >(mealApi.mealPickupMenuUrl, payload, signal);
  },
};

export default mealApi;
